/**
 * Energy-certificate-of-need flow DTO + completeness predicate (PF v2 native
 * module — Wave 2, module 2b).
 *
 * The module's durable state IS this DTO, stored at
 * `productConfigs["${cartItemId}|energy_certificate_of_need"]`
 * (MODULE-CONTRACT Pattern A). The base fields are BYTE-IDENTICAL to what the
 * legacy adapter serialized (`getEnergyCertificateRequestData`), so a draft
 * saved under the old adapter hydrates here with zero migration and `PF_VERSION`
 * stays frozen at 3. The additive optional `*Filenames` markers are NEW /
 * FE-only upload markers so the pure predicate can gate the upload requirements
 * WITHOUT reading live hub state (§4b). Absent on legacy drafts → default.
 * Written ONLY on user interaction (the value-guarded upload sync).
 *
 * ZERO React / store imports — `modules/completeness.ts` (which
 * `store/selectors.ts` imports) pulls `energyCertificateIsComplete` from here,
 * so this file MUST stay pure. Enum types come from `constants/purchaseFlow`
 * (allowed per contract §8 — NOT stranded under `PurchaseFlow/*`).
 */

import {
  BasementType,
  BuildingType,
  ConstructionType,
  GeneratedByType,
  GlobalShapeType,
  OrientationType,
  RoofPitchType,
  ThermalBridgeType,
  VentilationType,
  handoverAndPumpingFields
} from 'constants/purchaseFlow'
import { EnergyPassReason } from 'constants/purchaseFlow/sharedEnergyCertificateTypes'
import type { EnergyCertificateDetailsDTO, HeatingSystemItemDTO, WallPayloadDTO } from 'models/purchaseFlow/energyCertificateOfNeedDTO'

/**
 * Persisted/wire shape = the legacy `EnergyCertificateDetailsDTO` + additive
 * FE-only upload markers. Old drafts (no markers) hydrate fine; the module never
 * writes on mount so the round-trip stays byte-identical; new drafts carry the
 * markers. Future B2 submit serializer strips them (DF, owner BE/contract).
 */
export interface EnergyCertificateConfigDTO extends EnergyCertificateDetailsDTO {
  /** Gates `isAdditionalInformationValid` (ALWAYS). */
  exteriorViewFilenames?: string[]
  /** Gates `isAdditionalInformationValid` (ALWAYS). */
  heaterNameplateFilenames?: string[]
  /** Gates `isBuildingLayoutValid` ONLY when `buildingShape === OTHER` (marker is
   *  harmless for non-OTHER shapes). */
  buildingShapeFilenames?: string[]
  /** The optional construction description. Recorded so a resumed draft keeps it; gates nothing. */
  constructionDescriptionFilenames?: string[]
  /** Draft-only: the walls last entered for each shape, so switching back to a shape restores them. */
  wallDimensionsByShape?: Partial<Record<GlobalShapeType, WallPayloadDTO[]>>
  /**
   * Draft-only: did the client tick "ventilation system available"?
   *
   * The wire DTO has no field for the membership, so validity used to INFER it from whether any of
   * the three sibling fields was filled — which means ticking the box and filling nothing inferred
   * *not available* and the step passed, where legacy blocked. Recorded explicitly here and stripped
   * before placement (`stripDraftOnlyCertificateFields`), the same pattern the file-name markers
   * above use. Absent on drafts written before this, hence the inference fallback in
   * `ventilationSystemAvailable`.
   */
  hasVentilationSystem?: boolean
}

/** The collection-default heater (mirrors the legacy first-mount heater:
 *  `totalHeatingOutput:100`, `generatedBy:ELECTRIC`, empty combined Set). */
export const DEFAULT_HEATER: HeatingSystemItemDTO = {
  isHydraulicBalancing: false,
  heatingFuel: null,
  heatingFuelOption: null,
  woodPalletsEnergySource: null,
  heatingDesignation: '',
  heaterLocation: null,
  waterLocation: null,
  nightService: null,
  circulatingPumpType: null,
  heatTransferType: null,
  heatingPipeType: null,
  generatedBy: GeneratedByType.ELECTRIC,
  generatorYearOfManufacture: null,
  heatingPower: null,
  yearOfConstruction: null,
  decentralizedHeatingType: null,
  totalHeatingOutput: 100,
  heatingCombinedWith: new Set(),
  isHotWaterCirculation: false,
}

/** A freshly-added heater — legacy seeds `totalHeatingOutput:0` for every heater
 *  after the first (interacts with the `.every(Boolean)` gate: invalid until the
 *  user sets its share). */
export const NEW_HEATER: HeatingSystemItemDTO = { ...DEFAULT_HEATER, heatingCombinedWith: new Set(), totalHeatingOutput: 0 }

/**
 * Read-time fallback (field-merged at the ops-hook boundary; NEVER written on
 * mount). Mirrors the legacy sub-context defaults so an empty/undefined persisted
 * entry decodes to a view IDENTICAL to a fresh legacy mount (collection-defaults
 * for `heaters`/`wallDimensions` are applied in the codec on read).
 */
export const ENERGY_CERTIFICATE_DEFAULT: EnergyCertificateConfigDTO = {
  // Property information
  buildingType: null,
  livingUnits: 1,
  buildingLocation: null,
  totalLivingArea: 0,
  commercialSpaceInSquareMeters: 0,
  typeOfUseCommercial: '',
  // Building layout
  orientation: OrientationType.NORTH,
  buildingYear: '',
  buildingShape: GlobalShapeType.RECTANGLE,
  wallDimensions: [],
  // Construction method
  numberFullFloors: 1,
  roofCondition: null,
  floorHeightMeters: null,
  roofPitch: RoofPitchType.FLAT_ROOF,
  subsequentBasementInsulation: 0,
  subsequentRoofInsulation: 0,
  basement: null,
  soilDepth: null,
  heatedBasementArea: null,
  basementCeilingType: null,
  windowConstructionYear: null,
  windowType: null,
  constructionType: ConstructionType.MASSIVE_CONSTRUCTION,
  wallType: null,
  thermalBridges: ThermalBridgeType.STANDARD,
  // Energy and heating
  photovoltaics: null,
  hasWindowVentilation: true,
  airDistribution: null,
  ventilationType: null,
  fanType: null,
  heaters: [],
  heatRecovery: null,
  // Additional information
  reasonForEnergyPass: EnergyPassReason.NEW_RENTAL,
  messageToExpert: '',
  // FE-only upload markers
  exteriorViewFilenames: [],
  heaterNameplateFilenames: [],
  buildingShapeFilenames: [],
}

const HEATED_BASEMENTS: ReadonlySet<BasementType> = new Set([
  BasementType.HEATED_WINDOW_SOLAR_GAIN,
  BasementType.HEATED_NO_WINDOW,
])

// ─── The 5 sub-predicates (faithful port of context.ts:68-151) ──────────────
// Each is pure over the persisted DTO + markers. The chip strip drives per-tab
// validity badges off these; `energyCertificateIsComplete` is their conjunction.
// See spec §3 for the per-predicate subtleties + the documented behavior deltas.

/** PROPERTY_INFORMATION (context.ts:68-76). */
export const isPropertyInformationValid = (dto: EnergyCertificateConfigDTO): boolean => {
  if (!dto.buildingType) return false
  if (dto.buildingType === BuildingType.MIXED_USE_BUILDING && (!dto.commercialSpaceInSquareMeters || !dto.typeOfUseCommercial)) return false
  if (!dto.buildingLocation) return false
  // buildingYear is a string on the wire → the legacy `length===4` check ports directly.
  if (!dto.buildingYear || String(dto.buildingYear).length !== 4) return false
  if (!dto.totalLivingArea) return false
  return true
}

/** BUILDING_LAYOUT (context.ts:78-91). */
export const isBuildingLayoutValid = (dto: EnergyCertificateConfigDTO): boolean => {
  if (!dto.buildingShape || !dto.orientation) return false // dead guards (non-null defaults)
  if (dto.buildingShape !== GlobalShapeType.OTHER) {
    const walls = dto.wallDimensions ?? []
    // The walls are stored only once the client edits them, so none stored means none filled in.
    if (!walls.length) return false
    if (walls.some((w) => !w.length || (w.length as number) < 1)) return false
    if (walls.some((w) => w.subsequentlyInsulated && !w.wallInsulation)) return false
    return true
  }
  return !!dto.buildingShapeFilenames?.length
}

/** CONSTRUCTION_METHOD (context.ts:93-109). */
export const isConstructionMethodValid = (dto: EnergyCertificateConfigDTO): boolean => {
  if (!dto.constructionType) return false // dead guard
  // LIGHTWEIGHT ⇒ wallType (Lightweight); MASSIVE ⇒ wallType (Solid). Both map to the single `wallType`.
  if (!dto.wallType) return false
  if (!dto.roofCondition) return false
  if (!dto.roofPitch) return false // dead guard
  if (!dto.numberFullFloors) return false
  // Legacy `floorHeight !== ''`. `toNumber('')===0`, so clearing a previously-entered height stores 0,
  // which a `!= null` check would accept while legacy blocks empty — truthy check rejects 0/NaN (verify-energy SHOULD-FIX).
  if (!dto.floorHeightMeters) return false
  if (!dto.windowConstructionYear) return false
  if (!dto.windowType) return false
  if (!dto.thermalBridges) return false // dead guard
  if (!dto.basement) return false
  if (HEATED_BASEMENTS.has(dto.basement) && (!dto.soilDepth || !dto.heatedBasementArea)) return false
  if (!dto.basementCeilingType) return false
  return true
}

/**
 * Did the client tick "ventilation system available"?
 *
 * Prefers the explicit draft-only marker and falls back to field presence, which is what
 * this was before and is still the only signal a draft written earlier carries. The fallback is
 * one-directional on purpose: it can only say "available" when something was filled, so an old
 * draft behaves exactly as it did, while a new one that ticks the box and fills nothing is now
 * correctly incomplete instead of silently passing.
 *
 * @example
 * hasVentilationSystem({ hasVentilationSystem: true, airDistribution: null })  // true
 */
export const hasVentilationSystem = (dto: EnergyCertificateConfigDTO): boolean =>
  dto.hasVentilationSystem ?? (dto.airDistribution != null || dto.ventilationType != null || dto.fanType != null)

/** ENERGY_AND_HEATING (context.ts:111-145) — the heaviest, array-wide. */
export const isEnergyAndHeatingValid = (dto: EnergyCertificateConfigDTO): boolean => {
  // Photovoltaics: if present ⇒ area && peakPower && orientation.
  if (dto.photovoltaics && (!dto.photovoltaics.area || !dto.photovoltaics.peakPower || !dto.photovoltaics.geographicOrientation)) return false

  // Ventilation Set: reconstruct membership over the DTO. WINDOW ← hasWindowVentilation;
  // VENTILATION_SYSTEM_AVAILABLE comes from the draft-only marker, falling back to field presence.
  const ventilationSystemAvailable = hasVentilationSystem(dto)
  const ventilationSetSize = (dto.hasWindowVentilation ? 1 : 0) + (ventilationSystemAvailable ? 1 : 0)
  if (!ventilationSetSize) return false
  if (ventilationSystemAvailable) {
    if (!dto.airDistribution || !dto.ventilationType || !dto.fanType) return false
    if (dto.ventilationType !== VentilationType.EXHAUST_SYSTEM_NO_HEAT_RECOVERY && !dto.heatRecovery) return false
  }

  // Heaters (legacy always carries ≥1; the default heater has null fuel → invalid).
  const heaters = dto.heaters ?? []
  if (!heaters.length) return false

  // Array-wide handover: if ANY heater uses a handover fuel ⇒ EVERY heater needs the 3 handover fields.
  const sendHandover = heaters.some((h) => (h.heatingType ? handoverAndPumpingFields.has(h.heatingType) : false))
  if (sendHandover && heaters.some((h) => !h.circulatingPump || !h.heatingPipe || !h.heatTransfer)) return false

  // Hot water per heater, as the form, the encoder and the backend validator all decide it. The backend binds
  // every heater year as a non-null Int (`DemandCertificateHeaterDTO.yearOfConstruction`,
  // `CentralHotWaterDTO.constructionYear`, `DecentralizedHotWaterDTO.constructionYear`), so a null one 400s the
  // whole order (ISSUE-4191): every year the payload carries is required here.
  const isHotWaterComplete = (h: (typeof heaters)[number]): boolean => h.hasCentralHotWaterGeneration
    ? !!h.centralHotWater?.location && !!h.centralHotWater?.constructionYear
    : !!h.decentralizedHotWater?.energySource && !!h.decentralizedHotWater?.constructionYear && !!h.decentralizedHotWater?.type
  if (heaters.some((h) => !h.yearOfConstruction || !isHotWaterComplete(h))) return false

  // Per-heater required fields (⚠ `Boolean(0)` fails → a 0-share heater is invalid — reproduced).
  return heaters.every((h) => [h.heatingType, h.heatingSubType, h.location, h.nightSwitch, h.shareInTotalHeatingPower].every(Boolean))
}

/** ADDITIONAL_INFORMATION (context.ts:147-151) — the upload gate (markers). */
export const isAdditionalInformationValid = (dto: EnergyCertificateConfigDTO): boolean =>
  !!dto.exteriorViewFilenames?.length && !!dto.heaterNameplateFilenames?.length

/**
 * Pure completeness predicate — the conjunction of the 5 sub-predicates (legacy
 * `module.tsx:24`). Zero React/store; `modules/completeness.ts` imports this.
 */
export const energyCertificateIsComplete = (dto: EnergyCertificateConfigDTO | undefined): boolean =>
  !!dto &&
  isPropertyInformationValid(dto) &&
  isBuildingLayoutValid(dto) &&
  isConstructionMethodValid(dto) &&
  isEnergyAndHeatingValid(dto) &&
  isAdditionalInformationValid(dto)
